import { useUserStore } from '../stores/user';
import { useLoginStore } from '../stores/login';
import { usePublicStore } from '../stores/public';
import { useDoorStore } from '@/stores/door';
import { playTextVoice } from '../utils/voice';
import { sendLightCommand, getLightPlugin, LightCommand } from '@/utils/lightUtils';
import { addGlobalLightTask } from '../hooks/useLightManagement/useLightTaskQueue';
import { jumpRePages } from '@/utils/public';
import { autoPrintGoodsMeals } from '@/utils/print';
import dayjs from 'dayjs';
import { LogCat, logInfo } from '@/uni_modules/gp-logcat';
import { uploadFun } from '@/requestPub/api';
import moment from 'moment';
// #ifndef APP
import mqtt from 'mqtt';
// #endif

const prefix = 'billiard';
let mq = null;
let mqttPlugin = null;
const subscribedTopics = {
    shop: '',
    company: '',
    person: ''
};
const topic_all = {
    shop: '',
    company: `${prefix}/company/allshops`,
    person: `${prefix}/shop/user/`
};

// #ifdef APP
mqttPlugin = uni.requireNativePlugin('uni-mqtt-plugin');
// #endif

const isMqttConnected = () => {
    if (mqttPlugin) {
        return mqttPlugin.isConnected();
    }

    return !!mq;
};

const unsubscribeTopic = (topic) => {
    if (!topic || !isMqttConnected()) return;

    if (mqttPlugin) {
        mqttPlugin.unsubscribe(topic);
        return;
    }

    mq.unsubscribe(topic);
};

const subscribeTopic = (type, topic) => {
    if (!topic || !isMqttConnected()) return;
    if (subscribedTopics[type] === topic) return;

    unsubscribeTopic(subscribedTopics[type]);
    logInfo('订阅消息', topic);
    if (mqttPlugin) {
        mqttPlugin.subscribe(topic, 2);
    } else {
        mq.subscribe(topic, { qos: 2 });
    }

    subscribedTopics[type] = topic;
};

const routeMessage = (topic, message) => {
    if (topic === topic_all.company) return company_message(message);
    if (topic === topic_all.shop) return shop_message(message);
    if (topic === topic_all.person) return person_message(message);
};

const connect_mqtt = () => {
    return new Promise((resolve) => {
        const mqttInfo = useUserStore().mqttInfo;
        const host = mqttInfo.host.replace('wss', 'tcp') + ':1883';
        mqttPlugin.connect(
            {
                serverUri: host, // 例如 tcp://broker.emqx.io:1883
                clientId: mqttInfo.client_id + `_${Date.now()}`, // 客户端ID，必须唯一
                username: mqttInfo.username,
                password: mqttInfo.password,
                timeout: 3000, // 连接超时时间，毫秒
                keepAlive: 5, // 心跳间隔，秒
                cleanSession: false, // 是否清除会话,
                sessionExpiryInterval: 600 //缓存时长
            },
            (info) => {
                switch (info.type) {
                    case 'onConnectSuccess':
                        logInfo('连接成功', true);
                        resolve(true);
                        subscribe_with_user();
                        break;
                    case 'onConnectFailed':
                        uni.$emit('showToast', {
                            title: '信息渠道连接失败',
                            type: 'none',
                            duration: 1000
                        });
                        jumpRePages('/pages/login');
                        break;
                    case 'onMessageReceived':
                        routeMessage(info.data.topic, info.data.message);
                        break;
                    case 'onSubscribeSuccess':
                    default:
                        break;
                }
            }
        );
    });
};

export const init_mqtt = () => {
    logInfo('初始化mq');
    if (mqttPlugin) {
        return connect_mqtt();
    }

    try {
        if (mq) {
            mq.end();
            mq = null;
        }

        return new Promise((resolve) => {
            const mqttInfo = useUserStore().mqttInfo;
            if (!mqttInfo?.host) {
                resolve(false);
                return;
            }

            const host = mqttInfo.host;
            mq = mqtt.connect(`${host}/mqtt`, {
                username: mqttInfo.username,
                password: mqttInfo.password,
                port: mqttInfo.port,
                clientId: mqttInfo.client_id + `_${Date.now()}`,
                protocolVersion: 5,
                properties: {
                    sessionExpiryInterval: 600
                },
                keepalive: 60,
                qos: 2,
                clean: false
            });

            mq.on('connect', () => {
                logInfo('mq连接成功');
                console.log('mqtt链接成功');
                subscribe_with_user();
                resolve(true);
            });

            mq.on('message', (topic, message) => {
                routeMessage(topic, message);
            });

            mq.on('close', () => {
                console.log('mqtt链接关闭');
                logInfo('mq连接关闭');
            });

            mq.on('error', () => {
                console.log('mqtt链接错误');
                logInfo('mq连接错误');
            });
        });
    } catch (error) {
        console.log('mqtt链接异常', error);
    }
};

export const subscribe_with_user = async () => {
    if (mqttPlugin) {
        if (!mqttPlugin.isConnected()) {
            await init_mqtt();
        }
    } else if (!mq) {
        await init_mqtt();
    }

    const userInfo = useUserStore().userInfo;
    const userId = userInfo?.user_id;
    if (userId) {
        topic_all.person = `${prefix}/shop/user/${userId}`;
        subscribeTopic('person', topic_all.person);
        logInfo('订阅用户消息', topic_all.person);
    }
};

export const subscribe_with_shop = async () => {
    if (mqttPlugin) {
        if (!mqttPlugin.isConnected()) {
            await init_mqtt();
        }
    } else if (!mq) {
        await init_mqtt();
    }

    const currentShopInfo = useLoginStore().currentShopInfo;
    const config = usePublicStore().config;
    const shopId = currentShopInfo?.id;
    if (config?.hid && shopId) {
        topic_all.shop = `${prefix}/shop/audit/${config.hid}/${shopId}`;
        subscribeTopic('shop', topic_all.shop);
        console.log('订阅的消息', topic_all.shop);
    }

    if (topic_all.company) {
        subscribeTopic('company', topic_all.company);
    }
};

export const exquite_mqtt = () => {
    try {
        if (mqttPlugin) {
            if (mqttPlugin.isConnected()) {
                un_subscribe();
                mqttPlugin.disconnect();
            }
        } else if (mq) {
            un_subscribe();
            mq.end();
            mq = null;
        }
    } catch (error) {
        console.log(error);
    }
};

export const un_subscribe = () => {
    unsubscribeTopic(subscribedTopics.shop || topic_all.shop);
    unsubscribeTopic(subscribedTopics.company || topic_all.company);
    unsubscribeTopic(subscribedTopics.person || topic_all.person);
    subscribedTopics.shop = '';
    subscribedTopics.company = '';
    subscribedTopics.person = '';
};

const person_message = (message) => {
    const { data } = JSON.parse(message);
    if (data.channel == 'cashier') {
        const loginStore = useLoginStore();
        loginStore.exitLogin();
        uni.reLaunch({
            url: '/pages/login'
        });
    }
};

const company_message = (message) => {
    JSON.parse(message);
};

const shop_message = (message) => {
    const { type, data, server_time } = JSON.parse(message);
    const { config } = usePublicStore().config;
    console.log('-消息推送', data);
    logInfo('mqtt message:', { type, data });
    // 教练状态变更不弹出消息弹窗
    // if (type != 'update_coach_status') uni.$emit('showMsgPopup');
    if (!['update_coach_status', 'upload_log', 'shop_pay_success', 'light_control', 'bill_windup', 'light_command_data', 'shop_pay_failed'].includes(type))
        uni.$emit('showMsgPopup');
    if (data?.room_no) {
        data.room_no = String(data.room_no).replace(/-/g, '_');
    }

    switch (type) {
        case 'bill_windup':
            // playTextVoice(`${insertPauseMark(data.room_no)}，账单已结账`);
            useDoorStore().resetDoorFun(data?.bill_id);
            break;
        case 'room_call_self_order':
            if (config.self_open_order) {
                playTextVoice(`${insertPauseMark(data.room_no)}，已有顾客自助开台，请及时查看`);
            } else {
                playTextVoice(`${insertPauseMark(data.room_no)}，申请开台，请及时处理`);
            }
            break;
        case 'room_call_server':
            playTextVoice(`${insertPauseMark(data.room_no)}，呼叫服务`);
            break;
        case 'room_shop_order': {
            playTextVoice(`${insertPauseMark(data.room_no)},下单了商品，请及时处理`);
            const goodsdata = data?.data.map((item) => {
                // 处理 sku_name，如果 attributes 有内容则拼接
                let sku_name = item.sku_name;
                if (item.attributes && item.attributes.length > 0) {
                    const attrValues = item.attributes.map((attr) => attr.value).join('，');
                    sku_name = `${sku_name} ${attrValues}`;
                }
                return {
                    ...item,
                    goods: {
                        num: item.num,
                        goods_name: item.goods_name,
                        sku_name: sku_name
                    }
                };
            });
            const datas = {
                table_name: data.room_no,
                order_time: dayjs(server_time ? server_time * 1000 : new Date()).format('YYYY-MM-DD HH:mm:ss'),
                goods: goodsdata,
                meals: [],
                id: data.bill_id,
                name: '自助下单'
            };
            autoPrintGoodsMeals(datas);
            break;
        }
        case 'room_food_order': {
            playTextVoice(`${insertPauseMark(data.room_no)},下单了餐食，请及时处理`);
            const meals = data?.data.map((item) => {
                // 处理 sku_name，如果 attributes 有内容则拼接
                let sku_name = item.sku_name;
                if (item.attributes && item.attributes.length > 0) {
                    const attrValues = item.attributes.map((attr) => attr.value).join('，');
                    sku_name = `${sku_name} ${attrValues}`;
                }
                return {
                    ...item,
                    goods: {
                        num: item.num,
                        goods_name: item.goods_name,
                        sku_name: sku_name
                    }
                };
            });
            const datas = {
                table_name: data.room_no,
                order_time: dayjs(server_time ? server_time * 1000 : new Date()).format('YYYY-MM-DD HH:mm:ss'),
                goods: [],
                meals: meals,
                id: data.bill_id,
                name: '自助下单'
            };
            autoPrintGoodsMeals(datas);
            break;
        }
        case 'room_coach_order':
            playTextVoice(`${insertPauseMark(data.room_no)},有教练订单，请及时处理`);
            break;
        case 'update_coach_status':
            break;
        case 'room_charge_order':
            playTextVoice(`${insertPauseMark(data.room_no)},开始充电，请注意关注！`);
            break;
        case 'order_charge_complete':
            playTextVoice(`${insertPauseMark(data.room_no)},结束充电，请注意关注！`);
            break;
        case 'shop_pay_success':
            playTextVoice(`收款成功，${data.amount}元`);
            break;
        case 'shop_pay_failed':
            playTextVoice('收款失败，请核实');
            break;
        case 'room_advance_timeout_notice':
            uni.$emit('refreshTop');
            playTextVoice(`${insertPauseMark(data.room_no)}${data.content}`);
            break;
        case 'room_advance_renew_notice':
            playTextVoice(`${insertPauseMark(data.room_no)}${data.content}`);
            break;
        case 'room_expend_coach_timeout_notice':
            uni.$emit('refreshTop');
            playTextVoice(`${insertPauseMark(data.room_no)}${data.content}`);
            break;
        case 'light_control':
            Object.keys(getLightPlugin()).length > 0 && handleLightControlMessage(data);

            break;
        case 'light_command_data':
            Object.keys(getLightPlugin()).length > 0 && LightCommand(data);
            break;
        case 'upload_log':
            // #ifdef APP
            upload_files();
            // #endif
            break;

        // case 'shop_rbac_update':
        //     usePublicStore().getAuth(); //权限mq
        //     break;
    }
};

const handleLightControlMessage = (data) => {
    const { light_status, execute_at = 0, no = '', device_id } = data;
    const pubStore = usePublicStore();
    if (device_id !== pubStore.systemInfo.deviceCode && data.serial_port?.length) return;
    const cmd = (isOn) => sendLightCommand(isOn, { ...data, code: no, showToast: light_status === 1 });

    light_status === 1 ? cmd(true) : execute_at > 0 ? addGlobalLightTask(data) : cmd(false);
    uni.$emit('refreshDoorTable', data);
};

function insertPauseMark(str) {
    str = str.toString().toUpperCase();
    str = str.replace(/([0-9])(?=[0-9a-zA-Z])|([a-zA-Z])(?=[0-9a-zA-Z])/g, '$1$2、');
    const info = str.replace(/([\u4E00-\u9FA5])(?=[0-9a-zA-Z])|([0-9a-zA-Z])(?=[\u4E00-\u9FA5])/g, '$1$2、');

    return info.replace('间', '间(jian1)');
}
const upload_files = async () => {
    const result = LogCat.exportLogs([moment().format('YYYY-MM-DD')]);
    console.log(result);
    for (let s of result) {
        try {
            const result = await uploadFun(s, {}, 'cashier-log');
            console.log(result);
        } catch (error) {
            logInfo('上传失败', error);
        }
    }
};
